import type { MaybeValidProps } from "@core/domain/models/maybe-valid";
import type { Unbrand } from "@core/domain/types/unbrand.type";
import type { Id } from "@core/domain/value-objects/id.value-object";
import { zIdSchema } from "@core/domain/value-objects/id.value-object";
import { PlotSchema } from "@farm/domain/models/plot.model";
import type { ModelError, PartialValid } from "@iot-platform/core";
import {
    NonRecuperableModelError,
    RecuperableModelError,
    partialSafeParse,
} from "@iot-platform/core";
import { merge } from "es-toolkit";
import type { Result } from "neverthrow";
import { err, ok } from "neverthrow";
import * as z from "zod";

const FarmResumeSchema = z
    .object({
        id: zIdSchema,
        name: z.string().trim(),
    })
    .brand<"Farm">();

export type FarmResumeInputProps = z.input<typeof FarmResumeSchema>;
export type FarmResumeProps = z.output<typeof FarmResumeSchema>;

export class FarmResume {
    private constructor(private readonly props: Readonly<FarmResumeProps>) {}

    get id(): Id {
        return this.props.id;
    }

    get name(): string {
        return this.props.name;
    }

    static validate(
        props: Readonly<MaybeValidProps<FarmResumeInputProps>>,
    ): Result<FarmResumeProps, z.ZodError<FarmResumeProps>> {
        const result = FarmResumeSchema.safeParse(props);
        return result.success ? ok(result.data) : err(result.error);
    }

    private static partialValidate(
        props: Readonly<MaybeValidProps<FarmResumeInputProps>>,
    ): Result<FarmResumeProps, PartialValid<typeof FarmResume>> {
        const result = partialSafeParse(FarmResumeSchema, props);

        return result.success
            ? ok(result.data)
            : err({
                  validProps: result.validData,
                  errors: result.error,
              });
    }

    static create(
        props: Readonly<FarmResumeInputProps>,
    ): Result<FarmResume, z.ZodError<FarmResumeProps>> {
        return FarmResume.validate(props).map(
            (validatedProps) => new FarmResume(validatedProps),
        );
    }

    static createWithRecovery(
        props: FarmResumeInputProps,
    ): Result<FarmResume, ModelError<typeof FarmResume>> {
        return FarmResume.partialValidate(props)
            .mapErr((error) => {
                if (!error.validProps.id) return new NonRecuperableModelError();

                const defaultProps: Unbrand<FarmResumeProps> = {
                    id: error.validProps.id,
                    name: "",
                };

                const recoveryProps: Unbrand<FarmResumeProps> = merge(
                    defaultProps,
                    error.validProps,
                );

                return new RecuperableModelError<typeof FarmResume>(
                    new FarmResume(recoveryProps as FarmResumeProps),
                    error.errors,
                );
            })
            .map((validatedProps) => new FarmResume(validatedProps));
    }

    toCreateProps(): FarmResumeInputProps {
        return {
            id: this.props.id.toCreateProps(),
            name: this.props.name,
        };
    }
}

const FarmsSchema = z
    .array(
        z.custom<FarmResume>((val) => val instanceof FarmResume, {
            error: "Invalid FarmResume instance",
        }),
    )
    .brand<"Farms">();

export type FarmsInputProps = z.input<typeof FarmsSchema>;
export type FarmsProps = z.output<typeof FarmsSchema>;

export class Farms {
    private constructor(private readonly items: Readonly<FarmsProps>) {}

    get farms(): readonly Readonly<FarmResume>[] {
        return this.items;
    }

    static empty(): Farms {
        return new Farms(FarmsSchema.parse([]));
    }

    static validate(
        props: Readonly<MaybeValidProps<FarmsInputProps>>,
    ): Result<FarmsProps, z.ZodError<FarmsProps>> {
        const result = FarmsSchema.safeParse(props);
        return result.success ? ok(result.data) : err(result.error);
    }

    static create(
        props: Readonly<FarmsInputProps>,
    ): Result<Farms, z.ZodError<FarmsProps>> {
        return Farms.validate(props).map((validated) => new Farms(validated));
    }
}

/* ========= FARM DETAIL ========= */
const FarmUserSchema = z.object({
    id: z.number(),
    email: z.email(),
});

export type FarmUserProps = z.output<typeof FarmUserSchema>;

const FarmSchema = z
    .object({
        id: zIdSchema,
        name: z.string().trim(),
        enabledSatellites: z.array(z.string().trim()),
        plots: z.array(PlotSchema),
        users: z.array(FarmUserSchema),
    })
    .brand<"Farm">();

export type FarmInputProps = z.input<typeof FarmSchema>;
export type FarmProps = z.output<typeof FarmSchema>;

export class Farm {
    private constructor(private readonly props: Readonly<FarmProps>) {}

    get id(): Id {
        return this.props.id;
    }

    get name(): string {
        return this.props.name;
    }

    get enabledSatellites(): string[] {
        return this.props.enabledSatellites;
    }

    get hasSatellites(): boolean {
        return this.props.enabledSatellites.length > 0;
    }

    get plots(): z.output<typeof PlotSchema>[] {
        return this.props.plots;
    }

    get users(): readonly FarmUserProps[] {
        return this.props.users;
    }

    static validate(
        props: Readonly<MaybeValidProps<FarmInputProps>>,
    ): Result<FarmProps, z.ZodError<FarmProps>> {
        const result = FarmSchema.safeParse(props);
        return result.success ? ok(result.data) : err(result.error);
    }

    static create(
        props: Readonly<FarmInputProps>,
    ): Result<Farm, z.ZodError<FarmProps>> {
        return Farm.validate(props).map((validated) => new Farm(validated));
    }
}

export const FarmCreateSchema = z.object({
    name: z.string().trim().min(1),
    userIds: z.array(z.int().positive()),
    enabledSatellites: z.array(z.string().trim()),
});

export type FarmCreateInputProps = z.input<typeof FarmCreateSchema>;
export type FarmCreate = z.output<typeof FarmCreateSchema>;

export const FarmUpdateSchema = z.object({
    id: z.number().positive(),
    name: z.string().trim().min(1),
    userIds: z.array(z.int().positive()),
    enabledSatellites: z.array(z.string().trim()),
});
export type FarmUpdate = z.output<typeof FarmUpdateSchema>;
